"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import PublicLayout from "@/components/layout/PublicLayout";
import Link from "next/link";
import {
  ArrowRight,
  ArrowLeft,
  Building2,
  User,
  Lock,
  MapPin,
  Phone,
  Mail,
  Eye,
  EyeOff,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  Globe,
  FileText,
} from "lucide-react";
import type { ServiceMode } from "@/lib/service-mode";

export const dynamic = "force-dynamic";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function SignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const referralCode = searchParams.get("ref");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [verificationStatus, setVerificationStatus] = useState<"idle" | "verifying" | "success" | "error">("idle");
  const [form, setForm] = useState({
    // Step 1: Account
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    // Step 2: Garage Details
    garageName: "",
    phone: "",
    serviceMode: "GARAGE_ONLY" as ServiceMode,
    address: "",
    postcode: "",
    city: "",
    county: "",
    // Step 3: Optional
    shortDescription: "",
    description: "",
    website: "",
  });

  useEffect(() => {
    // Capture referral code from URL first
    if (referralCode) {
      console.log("Found referral code in URL:", referralCode);
      localStorage.setItem("referral_code", referralCode);
      localStorage.setItem("referral_code_timestamp", Date.now().toString());
      if (typeof window !== "undefined" && window.sessionStorage) {
        const trackingParams = { referralCode };
        sessionStorage.setItem("tracking_params", JSON.stringify(trackingParams));
      }
    }

    import("@/lib/referral-tracking").then(({ initializeReferralTracking }) => {
      initializeReferralTracking();
    });

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaReady(true);
      document.head.appendChild(script);
    } else {
      setRecaptchaReady(true);
    }
  }, [referralCode]);

  const validateStep = () => {
    setError(null);
    if (step === 1) {
      if (!form.name.trim() || !form.email.trim() || !form.password.trim() || !form.confirmPassword.trim()) {
        setError("Please complete all required fields");
        return false;
      }
      if (form.password.length < 8) {
        setError("Password must be at least 8 characters");
        return false;
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match");
        return false;
      }
    } else if (step === 2) {
      if (!form.garageName.trim() || !form.phone.trim()) {
        setError("Garage name and phone number are required");
        return false;
      }

      if (form.serviceMode === "MOBILE_ONLY") {
        if (!form.postcode.trim() && !form.city.trim() && !form.county.trim()) {
          setError("For mobile-only service, please provide at least one service area field (postcode, city, or county)");
          return false;
        }
      } else {
        if (!form.address.trim() || !form.postcode.trim() || !form.city.trim() || !form.county.trim()) {
          setError("Please complete all required workshop address fields");
          return false;
        }
      }

      if (!["GARAGE_ONLY", "MOBILE_ONLY", "HYBRID"].includes(form.serviceMode)) {
        setError("Please select a valid service mode");
        return false;
      }
    }
    return true;
  };

  const handleGeocodePostcode = async (postcode: string) => {
    if (!postcode || postcode.length < 5) {
      setVerificationStatus("idle");
      return;
    }
    setVerificationStatus("verifying");
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.result) {
          setVerificationStatus("success");
          if (data.result.admin_district) {
            setForm((prev) => ({ ...prev, city: data.result.admin_district }));
          }
          if (data.result.admin_county) {
            setForm((prev) => ({ ...prev, county: data.result.admin_county }));
          }
        }
      } else {
        setVerificationStatus("error");
      }
    } catch {
      setVerificationStatus("error");
    }
  };

  useEffect(() => {
    if (step === 2 && form.postcode.length >= 5) {
      const timeoutId = setTimeout(() => handleGeocodePostcode(form.postcode), 800);
      return () => clearTimeout(timeoutId);
    }
  }, [form.postcode, step]);

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setError(null);

    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("garage-data");
      localStorage.removeItem("user");

      let recaptchaToken = "";
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA") && window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "signup" });
      }

      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          recaptchaToken,
          garageName: form.garageName,
          phone: form.phone,
          serviceMode: form.serviceMode,
          address: form.address,
          postcode: form.postcode,
          city: form.city,
          county: form.county,
          shortDescription: form.shortDescription || null,
          description: form.description || null,
          website: form.website || null,
        }),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          setError(data.error || "Signup failed");
        } catch {
          setError(`Signup failed with status ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      if (data.garage) {
        localStorage.setItem("garage-data", JSON.stringify(data.garage));
      }

      const loginResponse = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          recaptchaToken: recaptchaToken || "",
        }),
      });

      if (loginResponse.ok) {
        const userData = await loginResponse.json();
        localStorage.setItem("user", JSON.stringify(userData.user));
        
        if (userData.garage) {
          localStorage.setItem("garage-data", JSON.stringify(userData.garage));
        } else if (userData.user.garages && userData.user.garages[0]) {
          localStorage.setItem("garage-data", JSON.stringify(userData.user.garages[0]));
        }

        try {
          const { recordReferralConversion } = await import("@/lib/referral-tracking");
          await recordReferralConversion(form.email, form.name);
        } catch (err) {
          console.error("failed to record referral conversion", err);
        }
        
        router.push("/garage/dashboard");
      } else {
        setError("Account created! Redirecting to login...");
        setTimeout(() => {
          router.push("/login");
        }, 1500);
      }
    } catch (error) {
      console.error("Signup error:", error);
      setError("An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="relative min-h-[90vh] bg-linear-to-br from-slate-950 via-slate-900 to-blue-950 flex items-center justify-center px-4 sm:px-6 py-12 md:py-20 overflow-hidden font-sans">
        
        {/* Ambient Glows */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

        <div className="relative w-full max-w-2xl space-y-6">
          
          {/* Header Title */}
          <div className="text-center space-y-2">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
              14-DAY FREE TRIAL • NO CREDIT CARD REQUIRED
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight">
              Start Your Free Trial
            </h1>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-3">
            {[
              { num: 1, label: "Account" },
              { num: 2, label: "Workshop" },
              { num: 3, label: "Details" },
            ].map((s) => (
              <div key={s.num} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs transition-all ${
                    s.num === step
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-500/40 ring-2 ring-blue-400/50"
                      : s.num < step
                      ? "bg-teal-500 text-slate-950 font-black"
                      : "bg-slate-800 text-slate-400 border border-slate-700"
                  }`}
                >
                  {s.num < step ? "✓" : s.num}
                </div>
                <span className={`text-xs font-semibold hidden sm:inline ${
                  s.num === step ? "text-white" : "text-slate-400"
                }`}>
                  {s.label}
                </span>
                {s.num < 3 && <div className={`w-6 h-0.5 ${s.num < step ? "bg-teal-500" : "bg-slate-800"}`} />}
              </div>
            ))}
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl p-7 sm:p-10 border-2 border-slate-200/90 shadow-2xl">
            
            {/* ── STEP 1: ACCOUNT DETAILS ── */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <div className="w-11 h-11 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
                    <User size={22} />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900">Your Account Details</h2>
                    <p className="text-xs text-slate-500">Step 1 of 3: Set up your manager login</p>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-medium">
                    {error}
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                      Your Full Name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="e.g. Sarah Jenkins"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="e.g. sarah@garage.co.uk"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Password (min 8 chars) *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })}
                          className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-0 px-3 text-slate-400 hover:text-slate-700 transition-colors"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Confirm Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={form.confirmPassword}
                          onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                          className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-0 px-3 text-slate-400 hover:text-slate-700 transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm sm:text-base cursor-pointer mt-4"
                  >
                    <span>Next: Workshop Details</span>
                    <ArrowRight size={18} />
                  </button>
                </form>
              </div>
            )}

            {/* ── STEP 2: GARAGE DETAILS ── */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <div className="w-11 h-11 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center shrink-0">
                    <Building2 size={22} />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900">Garage & Location Info</h2>
                    <p className="text-xs text-slate-500">Step 2 of 3: Connect your workshop location</p>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-medium">
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Garage / Business Name *
                      </label>
                      <input
                        type="text"
                        value={form.garageName}
                        onChange={(e) => setForm({ ...form, garageName: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="e.g. Apex Auto Repairs Ltd"
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Garage Phone *
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="020 7123 4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                      Service Mode *
                    </label>
                    <select
                      value={form.serviceMode}
                      onChange={(e) => setForm({ ...form, serviceMode: e.target.value as ServiceMode })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    >
                      <option value="GARAGE_ONLY">Physical Workshop Only (MOT Station / Service Bay)</option>
                      <option value="MOBILE_ONLY">Mobile Mechanic Only (Van / Call-out)</option>
                      <option value="HYBRID">Hybrid (Fixed Workshop + Mobile Van)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                      Street Address {form.serviceMode === "MOBILE_ONLY" ? "(optional)" : "*"}
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="142 High Street, Westminster"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Postcode *
                      </label>
                      <input
                        type="text"
                        value={form.postcode}
                        onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="SW1A 1AA"
                      />
                      {verificationStatus === "verifying" && <p className="text-[11px] text-blue-600 mt-1">Verifying postcode...</p>}
                      {verificationStatus === "success" && <p className="text-[11px] text-teal-600 font-bold mt-1">✓ Postcode verified</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        City / Town *
                      </label>
                      <input
                        type="text"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="London"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        County *
                      </label>
                      <input
                        type="text"
                        value={form.county}
                        onChange={(e) => setForm({ ...form, county: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="Greater London"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      className="flex-1 bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm sm:text-base cursor-pointer"
                    >
                      <span>Next: Workshop Bio</span>
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: OPTIONAL BIO & COMPLETION ── */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <div className="w-11 h-11 bg-cyan-50 text-cyan-600 rounded-xl flex items-center justify-center shrink-0">
                    <FileText size={22} />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900">About Your Garage</h2>
                    <p className="text-xs text-slate-500">Step 3 of 3: Optional bio & finish setup</p>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-medium">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                      Short Tagline (Optional)
                    </label>
                    <input
                      type="text"
                      value={form.shortDescription}
                      onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="e.g. Independent MOT & Bosch Diagnostic Specialists"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                      Workshop Website (Optional)
                    </label>
                    <input
                      type="text"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="e.g. https://apexautorepairs.co.uk"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm sm:text-base cursor-pointer disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Creating Workshop Account...</span>
                        </>
                      ) : (
                        <>
                          <span>Create Free Account</span>
                          <ArrowRight size={18} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-slate-100 text-center">
              <p className="text-xs text-slate-600">
                Already registered?{" "}
                <Link href="/login" className="text-blue-600 font-bold hover:underline">
                  Sign in here
                </Link>
              </p>
            </div>

          </div>

          {/* Trust points */}
          <div className="grid grid-cols-3 gap-2 text-center text-[11px] text-slate-400">
            <div className="flex items-center justify-center gap-1">
              <CheckCircle2 size={13} className="text-teal-400" /> Instant 2-min setup
            </div>
            <div className="flex items-center justify-center gap-1">
              <CheckCircle2 size={13} className="text-teal-400" /> Live DVSA Lookups
            </div>
            <div className="flex items-center justify-center gap-1">
              <CheckCircle2 size={13} className="text-teal-400" /> Cancel anytime
            </div>
          </div>

        </div>
      </div>
    </PublicLayout>
  );
}
